import { useState, useEffect } from 'react';
import { useAnalysisStore } from '@/store/analysisStore';
import { useOnboardingStore } from '@/store/onboardingStore';
import { Upload, X, Scan, Brain, Sparkles, Dumbbell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { TextureButton } from '@/components/ui/TextureButton';
import { GlassSurface } from '@/components/design/GlassSurface';
import { motion, AnimatePresence } from 'framer-motion';

export function VideoAnalysisPage() {
    const {
        videoFile, videoPreviewUrl, manualContext, status, result,
        setVideo, updateContext, startAnalysis, reset
    } = useAnalysisStore();

    // Personalization
    const { profile } = useOnboardingStore();
    // Fallback to "Demo Wrestler" if no name, just for the visual effect requested
    const displayName = profile.athleteId && profile.athleteId !== 'ath-1' ? (profile.athleteId) : "Demo Wrestler";
    // Map level/years to a cool title
    const levelTitle = profile.level === 'youth' ? "Level 1 — Rookie" :
        profile.level === 'middle' ? "Level 2 — Disciplined" :
            profile.level === 'highschool' ? "Level 3 — Varsity" :
                profile.level === 'college' ? "Level 4 — Elite" : "Level 2 — Disciplined";

    const [activeTab, setActiveTab] = useState<'upload' | 'manual'>('upload');
    const [scanProgress, setScanProgress] = useState(0);

    // Mock Scan Animation
    useEffect(() => {
        if (status === 'analyzing') {
            const interval = setInterval(() => {
                setScanProgress(prev => {
                    if (prev >= 100) {
                        clearInterval(interval);
                        return 100;
                    }
                    return prev + 1;
                });
            }, 50); // 5 seconds total
            return () => clearInterval(interval);
        } else {
            setScanProgress(0);
        }
    }, [status]);


    // Drag & Drop Handlers
    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file && file.type.startsWith('video/')) {
            setVideo(file);
        }
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) setVideo(file);
    };

    const handleAnalyze = () => {
        startAnalysis();
        // Simulate AI Processing time with scan animation
        setTimeout(() => {
            useAnalysisStore.getState().setResult({
                technicalFeedback: ["Stance too high on re-attacks", "Good heavy hands setup", "Hips too high on finish"],
                strategicAdjustments: ["Control the center earlier", "Hand fight more aggressively", "Snap down to create angles"],
                recommendedDrills: [
                    { name: "Stance Motion", focus: "Lower Level", reps: "3x 2min" },
                    { name: "Down Block -> Go Behind", focus: "Defense", reps: "50 reps" }
                ]
            });
        }, 5000);
    };

    return (
        <div className="min-h-screen bg-bg pb-24 px-4 pt-8 animate-fade-in relative overflow-x-hidden">

            {/* Ambient Background Elements */}
            <div className="fixed top-0 left-0 w-full h-[500px] bg-purple-900/20 blur-[100px] -z-10 pointer-events-none" />
            <div className="fixed bottom-0 right-0 w-[300px] h-[300px] bg-gold-500/10 blur-[80px] -z-10 pointer-events-none" />

            <div className="mb-10 pt-4 flex flex-col items-center text-center">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <h1 className="text-4xl md:text-5xl font-black italic text-white uppercase tracking-tighter mb-2 drop-shadow-2xl">
                        {displayName}
                    </h1>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="flex items-center gap-3"
                >
                    <div className="h-px w-12 bg-gradient-to-r from-transparent to-gold-500" />
                    <span className="text-sm font-bold text-gold-400 uppercase tracking-[0.2em] shadow-gold-glow">
                        {levelTitle}
                    </span>
                    <div className="h-px w-12 bg-gradient-to-l from-transparent to-gold-500" />
                </motion.div>
            </div>

            {/* Input Selection Tabs */}
            <div className="flex p-1 bg-bg-surface/50 backdrop-blur-md border border-white/5 rounded-2xl mb-8 relative">
                <button
                    onClick={() => setActiveTab('upload')}
                    className={cn(
                        "flex-1 py-3 text-sm font-bold rounded-xl transition-all relative z-10",
                        activeTab === 'upload' ? "text-white" : "text-text-muted hover:text-white"
                    )}
                >
                    Video Upload
                    {activeTab === 'upload' && (
                        <motion.div layoutId="tab-bg" className="absolute inset-0 bg-bg-elevated border border-white/10 rounded-xl shadow-lg -z-10" />
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('manual')}
                    className={cn(
                        "flex-1 py-3 text-sm font-bold rounded-xl transition-all relative z-10",
                        activeTab === 'manual' ? "text-white" : "text-text-muted hover:text-white"
                    )}
                >
                    Manual Entry
                    {activeTab === 'manual' && (
                        <motion.div layoutId="tab-bg" className="absolute inset-0 bg-bg-elevated border border-white/10 rounded-xl shadow-lg -z-10" />
                    )}
                </button>
            </div>

            <AnimatePresence mode="wait">
                {activeTab === 'upload' ? (
                    <motion.div
                        key="upload"
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 50 }}
                        transition={{ type: "spring", bounce: 0.2 }}
                        className="space-y-6"
                    >
                        {!videoFile ? (
                            <motion.div
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                onDrop={handleDrop}
                                onDragOver={(e) => e.preventDefault()}
                                className="border-2 border-dashed border-white/10 rounded-3xl h-72 flex flex-col items-center justify-center p-6 text-center hover:border-purple-500/50 hover:bg-purple-500/5 transition-all cursor-pointer group relative overflow-hidden"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                                <input
                                    type="file"
                                    accept="video/*"
                                    onChange={handleFileSelect}
                                    className="hidden"
                                    id="video-upload"
                                />
                                <label htmlFor="video-upload" className="flex flex-col items-center cursor-pointer w-full h-full justify-center relative z-10">
                                    <div className="w-20 h-20 rounded-full bg-bg-surface border border-white/10 flex items-center justify-center mb-6 shadow-2xl group-hover:shadow-purple-500/20 transition-all">
                                        <Upload className="text-text-muted group-hover:text-purple-400 transition-colors" size={32} />
                                    </div>
                                    <h3 className="text-white font-bold text-lg mb-2">Drop Match Footage</h3>
                                    <p className="text-xs text-text-muted max-w-[240px] leading-relaxed">
                                        AI will analyze your stance, shots, and defense frame-by-frame.
                                    </p>
                                </label>
                            </motion.div>
                        ) : (
                            <div className="relative rounded-3xl overflow-hidden bg-black aspect-video border border-white/10 shadow-2xl group">
                                <video src={videoPreviewUrl!} className="w-full h-full object-contain" />

                                {/* Overlay / Controls */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-6">
                                    <button
                                        onClick={() => setVideo(null)}
                                        className="px-6 py-2 bg-red-500/20 backdrop-blur-md border border-red-500/30 text-red-400 font-bold rounded-full hover:bg-red-500 hover:text-white transition-all"
                                    >
                                        Remove Video
                                    </button>
                                </div>

                                {/* Scanning Overlay */}
                                {status === 'analyzing' && (
                                    <div className="absolute inset-0 z-20 pointer-events-none">
                                        <motion.div
                                            animate={{ top: ['0%', '100%', '0%'] }}
                                            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                                            className="absolute left-0 right-0 h-1 bg-purple-500 shadow-[0_0_20px_rgba(168,85,247,0.8)]"
                                        />
                                        <div className="absolute inset-0 bg-purple-500/10 mix-blend-overlay" />

                                        <div className="absolute bottom-4 left-4 right-4">
                                            <div className="flex justify-between text-xs font-bold text-purple-300 uppercase mb-2">
                                                <span>Analyzing Technique...</span>
                                                <span>{scanProgress}%</span>
                                            </div>
                                            <div className="h-1 bg-black/50 rounded-full overflow-hidden">
                                                <motion.div
                                                    className="h-full bg-purple-500"
                                                    style={{ width: `${scanProgress}%` }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {!videoFile && (
                            <div className="flex items-start gap-4 p-4 bg-bg-surface/50 rounded-2xl border border-white/5">
                                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0">
                                    <Scan className="text-blue-400" size={16} />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-white mb-1">AI Vision Analysis</h4>
                                    <p className="text-xs text-text-muted leading-relaxed">
                                        Our multimodal model breaks down key sequences to identify technical flaws compared to elite wrestlers.
                                    </p>
                                </div>
                            </div>
                        )}

                    </motion.div>
                ) : (
                    <motion.div
                        key="manual"
                        initial={{ opacity: 0, x: 50 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -50 }}
                        transition={{ type: "spring", bounce: 0.2 }}
                        className="space-y-4"
                    >
                        <GlassSurface className="p-6 space-y-6">
                            <div>
                                <label className="text-[10px] font-bold text-gold-500 uppercase tracking-widest mb-2 block">Match Context</label>
                                <input
                                    type="text"
                                    value={manualContext.matchTitle}
                                    onChange={(e) => updateContext({ matchTitle: e.target.value })}
                                    placeholder="e.g. State Finals vs. Smith"
                                    className="w-full bg-bg-card border border-white/10 rounded-xl p-4 text-white placeholder:text-white/20 focus:border-gold-500/50 outline-none transition-colors font-medium"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-purple-400 uppercase tracking-widest mb-2 block flex items-center gap-2">
                                    <Brain size={12} />
                                    What Happened?
                                </label>
                                <textarea
                                    value={manualContext.keyMoments}
                                    onChange={(e) => updateContext({ keyMoments: e.target.value })}
                                    placeholder="Detailed description of the match. For example: 'I felt sluggish in the first period. He kept snapping me down. I lost 4-2 on a double leg takedown in overtime...'"
                                    className="w-full h-40 bg-bg-card border border-white/10 rounded-xl p-4 text-white placeholder:text-white/20 focus:border-purple-500/50 outline-none transition-colors resize-none leading-relaxed"
                                />
                            </div>

                            <div>
                                <label className="text-[10px] font-bold text-red-400 uppercase tracking-widest mb-2 block">Self Assessment (Weak Point)</label>
                                <input
                                    type="text"
                                    value={manualContext.focusArea}
                                    onChange={(e) => updateContext({ focusArea: e.target.value })}
                                    placeholder="e.g. Bottom defense, Hand fighting..."
                                    className="w-full bg-bg-card border border-white/10 rounded-xl p-4 text-white placeholder:text-white/20 focus:border-red-500/50 outline-none transition-colors font-medium"
                                />
                            </div>
                        </GlassSurface>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Analyze Button */}
            <motion.div
                className="fixed bottom-24 left-4 right-4 max-w-md mx-auto z-20"
                initial={{ y: 100 }}
                animate={{ y: 0 }}
                transition={{ delay: 0.5 }}
            >
                <TextureButton
                    onClick={handleAnalyze}
                    disabled={status === 'analyzing' || (!videoFile && !manualContext.keyMoments)}
                    className={cn(
                        "w-full shadow-[0_10px_40px_rgba(0,0,0,0.5)] border-t border-white/20",
                        status === 'analyzing' ? "bg-purple-600 border-purple-400" : ""
                    )}
                >
                    {status === 'analyzing' ? (
                        <span className="flex items-center gap-3">
                            <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Processing Match Data...
                        </span>
                    ) : (
                        <span className="flex items-center gap-2">
                            <Sparkles size={18} className={videoFile ? "text-purple-300" : "text-gold-300"} />
                            Generate Training Plan
                        </span>
                    )}
                </TextureButton>
            </motion.div>

            {/* Results Modal */}
            <AnimatePresence>
                {result && (
                    <motion.div
                        initial={{ opacity: 0, y: '100%' }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: '100%' }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className="fixed inset-0 z-50 bg-bg p-6 pt-12 overflow-y-auto"
                    >
                        <div className="absolute inset-0 bg-[url('/noise.svg')] opacity-5 pointer-events-none" />

                        <button onClick={reset} className="absolute top-6 right-6 p-2 bg-white/5 hover:bg-white/10 rounded-full transition-colors z-50">
                            <X className="text-white" />
                        </button>

                        <div className="max-w-md mx-auto space-y-8 pb-20">
                            <motion.div
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2 }}
                                className="text-center"
                            >
                                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-500/10 border border-purple-500/20 mb-4">
                                    <Brain className="text-purple-400 w-8 h-8" />
                                </div>
                                <h2 className="text-3xl font-black italic text-white uppercase tracking-tighter mb-2">Analysis Complete</h2>
                                <p className="text-text-muted">Coach Gable AI has identified <span className="text-white font-bold">3 key adjustments</span>.</p>
                            </motion.div>

                            <div className="space-y-6">
                                <section>
                                    <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                                        <Scan size={16} className="text-blue-400" />
                                        Technical Breakdown
                                    </h3>
                                    <div className="space-y-3">
                                        {result.technicalFeedback.map((item, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ x: -20, opacity: 0 }}
                                                animate={{ x: 0, opacity: 1 }}
                                                transition={{ delay: 0.3 + (i * 0.1) }}
                                                className="bg-bg-surface border border-white/5 p-4 rounded-xl flex gap-3"
                                            >
                                                <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 shrink-0" />
                                                <p className="text-sm text-gray-300 leading-relaxed">{item}</p>
                                            </motion.div>
                                        ))}
                                    </div>
                                </section>

                                <section>
                                    <h3 className="font-bold text-white mb-4 flex items-center gap-2">
                                        <Dumbbell size={16} className="text-gold-400" />
                                        Recommended Drills
                                    </h3>
                                    <div className="space-y-3">
                                        {result.recommendedDrills.map((drill, i) => (
                                            <motion.div
                                                key={i}
                                                initial={{ y: 20, opacity: 0 }}
                                                animate={{ y: 0, opacity: 1 }}
                                                transition={{ delay: 0.6 + (i * 0.1) }}
                                                className="bg-gradient-to-br from-gold-500/10 to-bg-surface border border-gold-500/20 p-4 rounded-xl relative overflow-hidden"
                                            >
                                                <div className="relative z-10">
                                                    <div className="font-black text-white text-lg uppercase italic mb-1">{drill.name}</div>
                                                    <div className="flex items-center gap-3 text-xs font-bold text-gold-400 uppercase tracking-widest">
                                                        <span>{drill.focus}</span>
                                                        <span className="w-1 h-1 rounded-full bg-gold-400" />
                                                        <span>{drill.reps}</span>
                                                    </div>
                                                </div>
                                                <div className="absolute right-0 bottom-0 top-0 w-24 bg-gradient-to-l from-gold-500/5 to-transparent pointer-events-none" />
                                            </motion.div>
                                        ))}
                                    </div>
                                </section>

                                <motion.button
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 1 }}
                                    className="w-full py-4 rounded-2xl bg-white text-black font-black uppercase tracking-wide hover:scale-[1.02] active:scale-[0.98] transition-all shadow-xl"
                                >
                                    Add to Training Schedule
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

        </div>
    );
}


